import { useQuery } from "@apollo/client";
import StaticContentSection from "components/company/StaticContentSection";
import formatMoneyValue from "components/utils/formatMoneyValue";
import { palette } from "lib/miolo";
import LineChart from "components/company/sections/summary/adornments/LineChart";
import type { CellContext } from "@tanstack/react-table";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "components/ui/Table";
import type {
  IFinancialBusinessSegmentsQuery,
  IFinancialBusinessSegmentsQueryVariables,
} from "lib/queries/__generated__/FinancialBusinessSegmentsQuery.generated";
import FinancialBusinessSegmentsQuery from "lib/queries/FinancialBusinessSegmentsQuery";
import parseDateYmd from "lib/utils/parseDateYmd";
import type { IFinancialBusinessSegment } from "types/graphqlTypes";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import { getYear } from "date-fns";
import ContentSectionDescriptionLine from "../ContentSectionDescriptionLine";
import ContentSectionHeading from "../ContentSectionHeading";
import Donut, { DEFAULT_DONUT_COLOR_PALETTE } from "../summary/adornments/Donut";

interface IAmericanOperatingSegmentsSectionProps {
  tickerSymbol: string;
}

export default function AmericanOperatingSegmentsSection({ tickerSymbol }: IAmericanOperatingSegmentsSectionProps) {
  const { data } = useQuery<IFinancialBusinessSegmentsQuery, IFinancialBusinessSegmentsQueryVariables>(
    FinancialBusinessSegmentsQuery,
    {
      variables: {
        tickerSymbol,
      },
    },
  );

  if (!data) {
    return null;
  }

  const { financialBusinessSegments } = data;

  if (!financialBusinessSegments) {
    return null;
  }

  const netRevenue = financialBusinessSegments.unadjustedTotals.find((node) => node.name === "Operating Revenue");

  if (!netRevenue) {
    return null;
  }

  return (
    <div className="w-full">
      <StaticContentSection primaryColor={palette.keppel} className="gap-5">
        <div className="col-span-12 flex flex-col sm:col-span-12 lg:col-span-8">
          <div>
            <ContentSectionHeading>Revenue Sources</ContentSectionHeading>
            <ContentSectionDescriptionLine>How the company makes money.</ContentSectionDescriptionLine>
          </div>

          <RevenueSourceBySegment
            header="Segment"
            financialBusinessSegments={
              financialBusinessSegments.segments.filter(
                (node) => node.__typename === "FinancialBusinessSegment",
              ) as IFinancialBusinessSegment[]
            }
            reportSource={financialBusinessSegments.reportSource}
            netRevenue={netRevenue.value}
            yearOfReportDate={getYear(parseDateYmd(financialBusinessSegments.periodEndingDate))}
          />
        </div>

        <div className="col-span-12 flex w-full justify-start lg:col-span-4 lg:justify-center">
          <div className="flex h-full flex-col">
            <div>
              <ContentSectionHeading>Revenue Growth</ContentSectionHeading>
              <ContentSectionDescriptionLine>In the last 10 years</ContentSectionDescriptionLine>
            </div>

            <div className="mt-10 flex">
              <LineChart
                chart={{
                  width: 320,
                  height: 170,
                  margin: { top: 16, left: 16, bottom: 24, right: 16 },
                  padding: { top: 4, left: 4, bottom: 4, right: 4 },
                  primaryColor: palette.oceanGreen,
                }}
                nodes={financialBusinessSegments.revenueHistory.map(({ periodEndingDate, value }) => ({
                  date: parseDateYmd(periodEndingDate),
                  value,
                }))}
              />
            </div>
          </div>
        </div>
      </StaticContentSection>
    </div>
  );
}

interface IRevenueSourceBySegmentProps {
  financialBusinessSegments: IFinancialBusinessSegment[];
  header: string;
  netRevenue: number;
  reportSource: {
    accessionNumber: string;
    primaryDocumentHref: any;
  } | null;
  yearOfReportDate: number;
}

function RevenueSourceBySegment({
  financialBusinessSegments,
  header,
  netRevenue,
  reportSource,
  yearOfReportDate,
}: IRevenueSourceBySegmentProps) {
  const segments = financialBusinessSegments
    .map((node) => {
      const indicator = node.ratioAdjustedIndicators.find((node) => node.name === "Operating Revenue");

      if (!indicator) {
        return null;
      }

      return {
        name: node.name,
        value: indicator.value,
      };
    })
    .filter(notUndefinedOrNull)
    .sort((a, b) => b.value - a.value);

  const columnHelper = createColumnHelper<{
    name: string;
    value: number;
  }>();
  const arrayOfColumns = [
    columnHelper.accessor("name", {
      header,
      meta: {
        header: {
          className: "font-museo-sans-condensed pl-2 bg-white text-left font-bold uppercase text-slate-800",
        },
        cell: {
          className:
            "sm:text-left font-museo-sans-condensed relative py-1 px-2 text-left text-sm font-medium text-slate-700",
        },
      },
    }),
    columnHelper.accessor("value", {
      header: "%",
      cell: ProportionLabel,
      meta: {
        header: {
          className: "font-museo-sans-condensed pr-2 bg-white text-right font-bold uppercase text-slate-800",
        },
        cell: {
          className: "sm:text-right font-museo-sans-condensed relative py-1 px-2 text-sm font-medium text-slate-700",
        },
      },
    }),
  ];

  return (
    <div className="flex w-full flex-col sm:flex-row sm:space-x-20">
      <div className="flex w-full flex-col">
        <div className="mt-2">
          <div className="grid gap-y-2 pr-6 text-sm font-medium tracking-wider sm:pr-0">
            <Table
              className="border-separate border-spacing-y-2"
              columns={arrayOfColumns}
              data={segments}
              isPaginationEnabled={true}
              isOddEven={true}
              pageSize={10}
              isSticky={false}
            />
          </div>
        </div>

        <div className="mt-auto">
          <div className="mt-4">
            <span className="font-museo-sans-condensed text-sm font-medium tracking-wider text-slate-500">
              Reference Year: <strong>{yearOfReportDate}</strong>
            </span>

            {reportSource && (
              <span className="cursor-pointer font-museo-sans-condensed text-sm font-medium tracking-widest text-slate-600">
                {" • "}
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={reportSource.primaryDocumentHref}
                  className="no-underline hover:underline"
                >
                  See more details
                </a>
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-start justify-start sm:justify-center">
        <Donut
          label={`$${formatMoneyValue(netRevenue, "short")}`}
          ariaLabel="Gráfico demonstrando a distribuição das Fontes de Receita"
          chart={{
            width: 200,
            height: 200,
          }}
          nodes={segments.map((node) => {
            return {
              key: node.name,
              value: node.value,
            };
          })}
        />
      </div>
    </div>
  );
}

function ProportionLabel({ getValue, row }: CellContext<{ name: string; value: number }, number>) {
  const value = getValue();

  let formated = "";
  if (value < 0.1) {
    formated = "<0.1%";
  }

  formated = value.toFixed(1).replace(".", ",");

  return (
    <span className="contents">
      {formated}
      <span
        className="absolute top-2/4 -right-10 h-3 w-3 -translate-y-2/4 rounded-xl sm:-right-5"
        css={{
          backgroundColor: DEFAULT_DONUT_COLOR_PALETTE[row.index],
        }}
      />
    </span>
  );
}
